import clsx from 'clsx';
import type React from 'react';
import { PricingCard } from '@/components/PricingPage/PricingCards/PricingCard';
import { usePricingCards } from './hooks/usePricingCards';
import type { Settings } from '@/types/settings';
import type { PricingPlan } from './hooks/usePricingCards';
import { cn } from '@/lib/utils';
interface PricingCardsProps {
  cardClassName?: string;
  className?: string;
  currency: Settings['currency'];
  /** See PricingCardProps — set on statically generated pages. */
  deferPriceUntilMounted?: boolean;
  highlightAnnualDiscount?: boolean;
  /** Subset of plans to render, e.g. Pro + Business on /pricing/business. */
  planTypes?: PricingPlan['type'][];
}
export const PricingCards: React.FC<PricingCardsProps> = ({
  currency,
  highlightAnnualDiscount = true,
  className = 'mx-auto max-w-lg md:max-w-2xl lg:max-w-6xl',
  cardClassName,
  deferPriceUntilMounted = false,
  planTypes
}) => {
  const {
    pricingPlans,
    isPricingError,
    refetchPricing,
    isActualPriceLoading
  } = usePricingCards(currency?.code, undefined, {
    planTypes
  });
  return <div className={cn(className, '@container')} data-testid="pricing-cards" id="plans" data-sentry-component="PricingCards" data-sentry-source-file="index.tsx">
      {!!isPricingError && <div className="mb-4 flex items-center justify-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-red-700 text-sm dark:bg-red-950 dark:text-red-300">
          <span>Something went wrong. Please try again.</span>
          <button className="font-medium underline hover:no-underline" onClick={() => refetchPricing()} type="button">
            Try again
          </button>
        </div>}
      <div className={cn(
    // Stacked, the highlight pill overhangs its card by 15px and a
    // 24px gutter left it all but touching the card above, so the
    // single-column row gap is wider (DEV-6728).
    'grid @xl:grid-cols-2 grid-cols-1 gap-x-6 @xl:gap-y-6 gap-y-10',
    // Track the number of plans actually rendered — the 2026 set drops
    // the Free card (four-up), the legacy set is four-up too, and five
    // columns only exist if Free and Business ever render together.
    // Both literals spelled out so Tailwind can see them.
    {
      '@5xl:grid-cols-5': pricingPlans.length === 5,
      '@5xl:grid-cols-4': pricingPlans.length === 4
    },
    // Two rows per row-of-cards, declared explicitly because subgrid
    // (see PricingCard) doesn't work over implicit tracks. Stacked
    // single-column needs none — a card is alone in its row.
    pricingPlans.length === 5 ? '@5xl:grid-rows-[repeat(2,auto)] @xl:grid-rows-[repeat(6,auto)]' : '@5xl:grid-rows-[repeat(2,auto)] @xl:grid-rows-[repeat(4,auto)]')}>
        {pricingPlans.map(plan => <PricingCard badgeLabel={plan.badgeLabel} className={clsx(
      // Free moves to the bottom of the single-column layout. Cards
      // span two rows each, so the last of N starts at row 2N-1.
      plan.type === 'Free' && (pricingPlans.length === 5 ? 'row-start-9 md:row-start-1' : 'row-start-7 md:row-start-1'), cardClassName)} currency={currency} deferPriceUntilMounted={deferPriceUntilMounted} description={plan.description} featureSections={plan.featureSections} highlightAnnualDiscount={highlightAnnualDiscount} isPopular={plan.isPopular} isPriceLoading={isActualPriceLoading(plan.type)} key={plan.type} period={plan.period} price={plan.price} priceNote={plan.priceNote} subtitle={plan.subtitle} subtitleIcon={plan.subtitleIcon} title={plan.title} type={plan.type} />)}
      </div>
    </div>;
};